import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  AudioLines,
  BookOpenText,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  Clock3,
  Copy,
  CreditCard,
  Download,
  Gauge,
  Gem,
  Globe2,
  Headphones,
  Languages,
  Library,
  LockKeyhole,
  LogOut,
  MailCheck,
  Menu,
  Mic2,
  Moon,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Save,
  Settings2,
  ShieldCheck,
  Sparkles,
  Square,
  Sun,
  Trash2,
  UploadCloud,
  UserRound,
  WandSparkles,
  X,
  Zap,
} from "lucide-react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
  updateProfile,
} from "firebase/auth";
import {
  createBillingPortal,
  createCloneVoice,
  createCreditCheckout,
  createSubscriptionCheckout,
  deleteCloneVoice,
  getBillingAccount,
  getCloneVoices,
  getUsage,
  syncBillingCheckout,
  synthesize,
} from "./api";
import {
  auth,
  firebaseConfigured,
  googleProvider,
} from "./firebase";
import {
  alternatePath,
  formatNumber,
  homePath,
  isJapanese,
  t,
} from "./i18n";
import type {
  BillingAccount,
  CloneVoice,
  CreditPack,
  Engine,
  GenerationSettings,
  SavedPreset,
  StudioVoice,
  SubscriptionPlan,
  Usage,
} from "./types";

const studioVoices: StudioVoice[] = [
  {
    id: "Ono_Anna",
    name: "Anna",
    description: "Playful, nimble, and warmly expressive",
    language: "Japanese",
    accent: "Tokyo",
    color: "#f4aa72",
    initials: "AN",
  },
  {
    id: "Ryan",
    name: "Ryan",
    description: "Dynamic with a confident rhythmic drive",
    language: "English",
    accent: "International",
    color: "#78b9b1",
    initials: "RY",
  },
  {
    id: "Aiden",
    name: "Aiden",
    description: "Sunny, clear, and naturally conversational",
    language: "English",
    accent: "American",
    color: "#b5a1db",
    initials: "AI",
  },
  {
    id: "Vivian",
    name: "Vivian",
    description: "Bright, youthful, and slightly editorial",
    language: "Chinese",
    accent: "Mandarin",
    color: "#dc8fa5",
    initials: "VI",
  },
  {
    id: "Serena",
    name: "Serena",
    description: "Warm, gentle, and softly cinematic",
    language: "Chinese",
    accent: "Mandarin",
    color: "#9fb5d9",
    initials: "SE",
  },
  {
    id: "Uncle_Fu",
    name: "Fu",
    description: "Seasoned, low, and mellow",
    language: "Chinese",
    accent: "Mandarin",
    color: "#c6a978",
    initials: "FU",
  },
  {
    id: "Dylan",
    name: "Dylan",
    description: "Youthful with an easy natural timbre",
    language: "Chinese",
    accent: "Beijing",
    color: "#7da6d8",
    initials: "DY",
  },
  {
    id: "Eric",
    name: "Eric",
    description: "Lively, husky, and bright",
    language: "Chinese",
    accent: "Sichuan",
    color: "#a5c787",
    initials: "ER",
  },
  {
    id: "Sohee",
    name: "Sohee",
    description: "Warm and emotionally rich",
    language: "Korean",
    accent: "Seoul",
    color: "#d4a6ce",
    initials: "SO",
  },
];

const languages = [
  "Auto",
  "English",
  "Japanese",
  "Chinese",
  "Korean",
  "German",
  "French",
  "Spanish",
  "Italian",
  "Portuguese",
  "Russian",
];

const samples = [
  {
    label: "Quiet wonder",
    text: isJapanese
      ? "夜明けの光が街をそっと起こすころ、新しい一日が静かに声を上げました。"
      : "Somewhere between the first light and the waking city, the day found its voice.",
  },
  {
    label: "Japanese welcome",
    text: "声には、言葉だけでは伝えられない温度があります。SuraYomiで、あなたの物語を聞かせてください。",
  },
  {
    label: "Product story",
    text: isJapanese
      ? "想いの美しさを、そのまま声へ。SuraYomiなら、明瞭で表情豊かな音声を、わずかな操作で作成できます。"
      : "Meet the ideas that sound as beautiful as they feel. Clear, expressive speech—crafted in a few thoughtful seconds.",
  },
];

const defaultSettings: GenerationSettings = {
  engine: "custom",
  speaker: "Ono_Anna",
  language: "Auto",
  prompt: isJapanese
    ? "自然で親しみのある語り口で、やさしく表情をつけながら、落ち着いた速さで読んでください。"
    : "Natural, intimate, and gently expressive. Keep a calm pace.",
  speed: 1,
  temperature: 0.85,
  topP: 0.95,
  topK: 50,
  repetitionPenalty: 1.05,
  seed: 2407,
  format: "mp3",
};

const charactersPerCredit = 10;
const creditsForText = (value: string, engine: Engine) => {
  const baseCredits = Math.ceil(value.length / charactersPerCredit);
  return engine === "clone" ? Math.ceil(baseCredits * 1.5) : baseCredits;
};

const pricing = [
  {
    id: "free" as const,
    name: "Free",
    price: "$0",
    cadence: "forever",
    description: "For trying ideas and short reads.",
    features: [
      "50 credits / month",
      "All 9 studio voices",
      "Voice design & cloning",
      "WAV and MP3 downloads",
    ],
  },
  {
    id: "creator" as const,
    name: "Creator",
    price: "$20",
    cadence: "/ month",
    description: "For regular creative projects.",
    features: [
      "3,000 credits / month",
      "Extra credit refills",
      "All 9 studio voices",
      "WAV and MP3 downloads",
    ],
    featured: true,
  },
  {
    id: "studio" as const,
    name: "Studio",
    price: "$40",
    cadence: "/ month",
    description: "For frequent and longer-form projects.",
    features: [
      "8,000 credits / month",
      "Extra credit refills",
      "All 9 studio voices",
      "WAV and MP3 downloads",
    ],
  },
];

function BrandMark({ small = false }: { small?: boolean }) {
  return (
    <div className={`brand-mark ${small ? "brand-mark--small" : ""}`} aria-hidden>
      <span />
      <span />
      <span />
      <span />
      <span />
    </div>
  );
}

function Logo() {
  return (
    <a className="logo" href={`${homePath}#top`} aria-label={t("SuraYomi home")}>
      <BrandMark small />
      <span>SuraYomi</span>
    </a>
  );
}

function Modal({
  children,
  onClose,
  label,
}: {
  children: ReactNode;
  onClose: () => void;
  label: string;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="icon-button modal-close" onClick={onClose} aria-label={t("Close")}>
          <X size={18} />
        </button>
        {children}
      </section>
    </div>
  );
}

function AuthModal({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<"signin" | "register">("signin");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");

  const continueWithGoogle = async () => {
    if (!auth) return;
    setBusy(true);
    setError("");
    try {
      await signInWithPopup(auth, googleProvider);
      onClose();
    } catch (caught) {
      setError(friendlyAuthError(caught));
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (event: FormEvent) => {
    event.preventDefault();
    if (!auth) return;
    setBusy(true);
    setError("");
    try {
      if (auth) auth.languageCode = isJapanese ? "ja" : "en";
      if (mode === "register") {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        const cleanDisplayName = displayName.trim().replace(/\s+/g, " ").slice(0, 60);
        if (cleanDisplayName) {
          try {
            await updateProfile(credential.user, { displayName: cleanDisplayName });
          } catch {
            // Account verification must continue; the profile has an email-based fallback.
          }
        }
        await sendEmailVerification(credential.user, {
          url: `${window.location.origin}${homePath}`,
        });
        setVerificationEmail(credential.user.email ?? email);
        await signOut(auth);
      } else {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        if (!credential.user.emailVerified) {
          await sendEmailVerification(credential.user, {
            url: `${window.location.origin}${homePath}`,
          });
          setVerificationEmail(credential.user.email ?? email);
          await signOut(auth);
        } else {
          onClose();
        }
      }
    } catch (caught) {
      setError(friendlyAuthError(caught));
    } finally {
      setBusy(false);
    }
  };

  if (verificationEmail) {
    return (
      <Modal onClose={onClose} label={t("Verify your email")}>
        <div className="auth-modal verification-state">
          <span className="verification-icon"><MailCheck size={25} /></span>
          <p className="eyebrow">{t("ONE QUICK STEP")}</p>
          <h2>{t("Check your inbox.")}</h2>
          <p className="modal-copy">
            {t("We sent a verification link to")} <strong>{verificationEmail}</strong>.{" "}
            {t("Verify your address, then return here and sign in.")}
          </p>
          <button
            className="primary-button auth-submit"
            onClick={() => {
              setVerificationEmail("");
              setMode("signin");
              setPassword("");
            }}
          >
            {t("Back to sign in")}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} label={t(mode === "signin" ? "Sign in" : "Create account")}>
      <div className="auth-modal">
        <BrandMark />
        <p className="eyebrow">{t("YOUR SURAYOMI STUDIO")}</p>
        <h2>{t(mode === "signin" ? "Welcome back." : "Bring your words to life.")}</h2>
        <p className="modal-copy">
          {t(mode === "signin"
            ? "Sign in to create, save, and return to your favorite voices."
            : "Start with 50 credits each month—no card needed.")}
        </p>
        {!firebaseConfigured && (
          <div className="notice notice--warning">
            {t("Authentication is being connected. Please check back in a moment.")}
          </div>
        )}
        <button
          className="google-button"
          onClick={continueWithGoogle}
          disabled={busy || !firebaseConfigured}
        >
          <span className="google-g">G</span>
          {t("Continue with Google")}
        </button>
        <div className="divider"><span>{t("or continue with email")}</span></div>
        <form onSubmit={submitEmail} className="auth-form">
          {mode === "register" && (
            <label>
              <span>{t("Display name")}</span>
              <input
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder={t("How should we address you?")}
                maxLength={60}
                required
              />
            </label>
          )}
          <label>
            <span>{t("Email address")}</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>
          <label>
            <span>{t("Password")}</span>
            <input
              type="password"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("At least 6 characters")}
              minLength={6}
              required
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button auth-submit" disabled={busy || !firebaseConfigured}>
            {busy ? <RefreshCw className="spin" size={17} /> : null}
            {t(mode === "signin" ? "Sign in" : "Create free account")}
          </button>
        </form>
        <p className="auth-switch">
          {t(mode === "signin" ? "New to SuraYomi?" : "Already have an account?")}{" "}
          <button onClick={() => setMode(mode === "signin" ? "register" : "signin")}>
            {t(mode === "signin" ? "Create an account" : "Sign in")}
          </button>
        </p>
        <p className="auth-legal">
          {isJapanese ? (
            <>
              続行すると、<a href="/ja/terms/">利用規約</a>に同意し、
              <a href="/ja/privacy/">プライバシーポリシー</a>を確認したものとみなされます。
            </>
          ) : (
            <>
              By continuing, you agree to our <a href="/terms/">Terms</a> and acknowledge our{" "}
              <a href="/privacy/">Privacy Policy</a>.
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}

function friendlyAuthError(caught: unknown): string {
  const code =
    typeof caught === "object" && caught && "code" in caught
      ? String((caught as { code: unknown }).code)
      : "";
  if (code.includes("invalid-credential")) return t("That email or password doesn’t match.");
  if (code.includes("email-already-in-use")) return t("That email already has an account.");
  if (code.includes("popup-closed")) return t("The sign-in window was closed.");
  if (code.includes("weak-password")) return t("Please choose a stronger password.");
  return t("We couldn’t sign you in just now. Please try again.");
}

function friendlyProfileName(user: User): string {
  const displayName = user.displayName?.trim().replace(/\s+/g, " ");
  if (displayName) return displayName.slice(0, 60);

  const emailName = user.email?.split("@", 1)[0]?.split("+", 1)[0] ?? "";
  const words = emailName
    .split(/[._-]+/)
    .map((word) => word.trim())
    .filter(Boolean)
    .slice(0, 4);
  const friendlyName = words
    .map((word) =>
      /^[a-z]/.test(word) ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word,
    )
    .join(" ");

  return friendlyName.slice(0, 60) || t("Account");
}

function RangeField({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (value: number) => void;
  hint?: string;
}) {
  const progress = ((value - min) / (max - min)) * 100;
  return (
    <label className="range-field">
      <span className="range-label">
        <span>{label}{hint && <span className="tiny-hint" title={hint}>?</span>}</span>
        <strong>{display}</strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--range-progress": `${progress}%` } as React.CSSProperties}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function ModeButton({
  active,
  icon,
  label,
  caption,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  caption: string;
  onClick: () => void;
}) {
  return (
    <button className={`mode-button ${active ? "mode-button--active" : ""}`} onClick={onClick}>
      <span className="mode-icon">{icon}</span>
      <span><strong>{label}</strong><small>{caption}</small></span>
      {active && <CheckCircle2 className="mode-check" size={18} />}
    </button>
  );
}

function Waveform({ active = false }: { active?: boolean }) {
  const bars = useMemo(
    () => [12, 22, 17, 31, 42, 24, 36, 51, 27, 19, 44, 61, 37, 24, 53, 41, 68, 34, 28, 49, 57, 31, 47, 23, 38, 55, 29, 43, 20, 34, 48, 26, 16, 31, 22, 13],
    [],
  );
  return (
    <div className={`waveform ${active ? "waveform--active" : ""}`} aria-hidden>
      {bars.map((height, index) => (
        <span key={index} style={{ height: `${height}px`, animationDelay: `${index * 24}ms` }} />
      ))}
    </div>
  );
}

function AudioResult({
  url,
  filename,
  onClear,
}: {
  url: string;
  filename: string;
  onClear: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = async () => {
    const element = audioRef.current;
    if (!element) return;
    if (element.paused) await element.play();
    else element.pause();
  };

  return (
    <div className="audio-result">
      <audio
        ref={audioRef}
        src={url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
        onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
      />
      <button className="audio-play" onClick={toggle} aria-label={t(playing ? "Pause" : "Play")}>
        {playing ? <Pause size={19} fill="currentColor" /> : <Play size={19} fill="currentColor" />}
      </button>
      <div className="audio-main">
        <div className="audio-title">
          <span>{filename}</span>
          <span>{formatTime(elapsed)} / {formatTime(duration)}</span>
        </div>
        <div className="mini-wave">
          {Array.from({ length: 54 }, (_, index) => (
            <span
              key={index}
              className={duration && index / 54 < elapsed / duration ? "passed" : ""}
              style={{ height: `${8 + ((index * 17) % 19)}px` }}
            />
          ))}
        </div>
      </div>
      <a className="download-button" href={url} download={filename}>
        <Download size={17} />
        {t("Download")}
      </a>
      <button className="icon-button" onClick={onClear} aria-label={t("Remove audio")}>
        <X size={17} />
      </button>
    </div>
  );
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${Math.floor(seconds % 60).toString().padStart(2, "0")}`;
}

function formatBillingDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(isJapanese ? "ja-JP" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function PresetMenu({
  presets,
  onApply,
  onDelete,
}: {
  presets: SavedPreset[];
  onApply: (preset: SavedPreset) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="preset-menu">
      <div className="preset-menu-head">
        <span>{t("Saved presets")}</span>
        <small>{presets.length}/12</small>
      </div>
      {presets.length === 0 ? (
        <div className="preset-empty">
          <Save size={20} />
          <p>{t("Save a preset to reuse its voice, controls, and seed.")}</p>
        </div>
      ) : (
        presets.map((preset) => (
          <div className="preset-row" key={preset.id}>
            <button onClick={() => onApply(preset)}>
              <span>{preset.name}</span>
              <small>
                {t(
                  preset.settings.engine === "custom"
                    ? "Studio voice"
                    : preset.settings.engine === "design"
                      ? "Design a voice"
                      : "Clone a voice",
                )} · {t("Voice seed")} {preset.settings.seed}
              </small>
            </button>
            <button
              className="preset-delete"
              onClick={() => onDelete(preset.id)}
              aria-label={`${t("Delete preset")}: ${preset.name}`}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function SavePresetModal({
  onSave,
  onClose,
}: {
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  return (
    <Modal onClose={onClose} label={t("Save voice preset")}>
      <div className="small-modal">
        <div className="modal-icon"><Save size={21} /></div>
        <p className="eyebrow">{t("VOICE PRESET")}</p>
        <h2>{t("Save this sound.")}</h2>
        <p className="modal-copy">{t("Keep every detail—including the seed—ready for your next read.")}</p>
        <label className="standalone-field">
          <span>{t("Preset name")}</span>
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("Warm documentary")}
            maxLength={40}
            onKeyDown={(event) => {
              if (event.key === "Enter" && name.trim()) onSave(name.trim());
            }}
          />
        </label>
        <button className="primary-button auth-submit" disabled={!name.trim()} onClick={() => onSave(name.trim())}>
          {t("Save preset")}
        </button>
      </div>
    </Modal>
  );
}

function VoiceClonePanel({
  user,
  voices,
  selectedId,
  onSelect,
  onCreated,
  onDeleted,
  onSignIn,
}: {
  user: User | null;
  voices: CloneVoice[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onCreated: (voice: CloneVoice) => void;
  onDeleted: (id: string) => void;
  onSignIn: () => void;
}) {
  const [expanded, setExpanded] = useState(voices.length === 0);
  const [sourceMode, setSourceMode] = useState<"record" | "upload">("record");
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [referenceText, setReferenceText] = useState("");
  const [language, setLanguage] = useState("Auto");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedSeconds, setRecordedSeconds] = useState(0);
  const [recordingUrl, setRecordingUrl] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStartedRef = useRef(0);
  const recordingTimerRef = useRef<number | null>(null);
  const recordingSupported =
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  const stopMediaTracks = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  };

  const clearRecordingTimer = () => {
    if (recordingTimerRef.current !== null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const stopRecording = () => {
    clearRecordingTimer();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      stopMediaTracks();
      setRecording(false);
    }
  };

  const clearRecording = () => {
    if (recording) stopRecording();
    setFile(null);
    setRecordedSeconds(0);
    setRecordingSeconds(0);
    setRecordingUrl("");
    recordingChunksRef.current = [];
  };

  useEffect(() => {
    return () => {
      clearRecordingTimer();
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") recorder.stop();
      stopMediaTracks();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (recordingUrl) URL.revokeObjectURL(recordingUrl);
    };
  }, [recordingUrl]);

  const startRecording = async () => {
    if (!recordingSupported) {
      setError(t("Recording is not supported in this browser. You can still upload a clip."));
      return;
    }
    setError("");
    clearRecording();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      mediaStreamRef.current = stream;
      const recordingTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mimeType =
        typeof MediaRecorder.isTypeSupported === "function"
          ? recordingTypes.find((type) => MediaRecorder.isTypeSupported(type))
          : "audio/mp4";
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      recordingStartedRef.current = Date.now();
      setRecordingSeconds(0);
      setRecordedSeconds(0);
      setRecording(true);

      recorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) recordingChunksRef.current.push(event.data);
      });
      recorder.addEventListener("stop", () => {
        clearRecordingTimer();
        stopMediaTracks();
        setRecording(false);
        const duration = Math.min(
          20,
          Math.max(0.1, (Date.now() - recordingStartedRef.current) / 1000),
        );
        setRecordingSeconds(duration);
        setRecordedSeconds(duration);
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(recordingChunksRef.current, { type });
        if (blob.size === 0) {
          setError(t("We couldn’t capture audio. Please try again or upload a clip."));
          return;
        }
        const extension = type.includes("mp4")
          ? "m4a"
          : type.includes("ogg")
            ? "ogg"
            : "webm";
        const recordedFile = new File(
          [blob],
          `surayomi-voice-${Date.now()}.${extension}`,
          { type },
        );
        setFile(recordedFile);
        setRecordingUrl(URL.createObjectURL(blob));
        if (duration < 5) {
          setError(t("Record at least 5 seconds for a clear voice."));
        }
      });
      recorder.addEventListener("error", () => {
        clearRecordingTimer();
        stopMediaTracks();
        setRecording(false);
        setError(t("We couldn’t capture audio. Please try again or upload a clip."));
      });
      // Keep the short recording in one finalized blob. WebKit has
      // historically produced unreadable fragmented MP4 when a small
      // MediaRecorder timeslice is used, especially on iPhone.
      recorder.start();
      recordingTimerRef.current = window.setInterval(() => {
        const elapsed = Math.min(20, (Date.now() - recordingStartedRef.current) / 1000);
        setRecordingSeconds(elapsed);
        if (elapsed >= 20 && recorder.state !== "inactive") {
          clearRecordingTimer();
          recorder.stop();
        }
      }, 100);
    } catch (caught) {
      clearRecordingTimer();
      stopMediaTracks();
      setRecording(false);
      const denied =
        caught instanceof DOMException &&
        (caught.name === "NotAllowedError" || caught.name === "PermissionDeniedError");
      setError(
        denied
          ? t("Microphone access was blocked. Allow it in your browser settings or upload a clip.")
          : t("We couldn’t open the microphone. Please try again or upload a clip."),
      );
    }
  };

  const saveVoice = async () => {
    if (!user) {
      onSignIn();
      return;
    }
    if (!file) {
      setError(t("Record or upload a voice sample first."));
      return;
    }
    if (sourceMode === "record" && recordedSeconds < 5) {
      setError(t("Record at least 5 seconds for a clear voice."));
      return;
    }
    if (!name.trim()) {
      setError(t("Give this voice a name before saving."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const voice = await createCloneVoice(user, {
        name: name.trim(),
        language,
        referenceText: referenceText.trim(),
        audio: file,
      });
      onCreated(voice);
      onSelect(voice.id);
      setExpanded(false);
      setFile(null);
      setRecordedSeconds(0);
      setRecordingSeconds(0);
      setRecordingUrl("");
      setName("");
      setReferenceText("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("We couldn’t save that voice."));
    } finally {
      setBusy(false);
    }
  };

  const removeVoice = async (id: string) => {
    if (!user) return;
    try {
      await deleteCloneVoice(user, id);
      onDeleted(id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("We couldn’t remove that voice."));
    }
  };

  return (
    <div className="clone-panel">
      {voices.length > 0 && (
        <div className="clone-library">
          <div className="section-label-row">
            <span className="field-label">{t("Your voices")}</span>
            <button className="text-button" onClick={() => setExpanded(!expanded)}>
              <Plus size={14} /> {t("New voice")}
            </button>
          </div>
          <div className="clone-list">
            {voices.map((voice) => (
              <div className={`clone-card ${selectedId === voice.id ? "clone-card--active" : ""}`} key={voice.id}>
                <button onClick={() => onSelect(voice.id)}>
                  <span className="clone-avatar"><Mic2 size={16} /></span>
                  <span><strong>{voice.name}</strong><small>{voice.language}</small></span>
                  {selectedId === voice.id && <Check size={16} />}
                </button>
                <button className="clone-delete" onClick={() => void removeVoice(voice.id)} aria-label={`Delete ${voice.name}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {expanded && (
        <div className="clone-create">
          <div className="clone-intro">
            <span className="clone-intro-icon"><AudioLines size={18} /></span>
            <div>
              <strong>{t("Make a reusable voice")}</strong>
              <p>{t("Use 5–20 seconds of clear, single-speaker audio. Only clone a voice you have permission to use.")}</p>
            </div>
          </div>
          <p className="clone-credit-note">
            <Gem size={13} /> {t("Voice cloning uses 1.5× credits.")}
          </p>
          <div className="clone-source-tabs" role="tablist" aria-label={t("Voice sample source")}>
            <button
              type="button"
              role="tab"
              aria-selected={sourceMode === "record"}
              className={sourceMode === "record" ? "active" : ""}
              onClick={() => {
                setSourceMode("record");
                setError("");
                setFile(recordingUrl ? file : null);
              }}
              disabled={recording}
            >
              <Mic2 size={14} /> {t("Record here")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sourceMode === "upload"}
              className={sourceMode === "upload" ? "active" : ""}
              onClick={() => {
                setSourceMode("upload");
                setError("");
                clearRecording();
              }}
              disabled={recording}
            >
              <UploadCloud size={14} /> {t("Upload a clip")}
            </button>
          </div>

          {sourceMode === "record" ? (
            <div className={`voice-recorder ${recording ? "voice-recorder--active" : ""}`}>
              {!recordingUrl ? (
                <>
                  <button
                    type="button"
                    className="record-button"
                    onClick={() => recording ? stopRecording() : void startRecording()}
                    disabled={!recordingSupported}
                    aria-label={t(recording ? "Stop recording" : "Start recording")}
                  >
                    <span className="record-button-ring" />
                    {recording ? <Square size={19} fill="currentColor" /> : <Mic2 size={21} />}
                  </button>
                  <div className="record-copy">
                    <strong>{t(recording ? "Listening…" : "Record a clean voice sample")}</strong>
                    <span>
                      {recording
                        ? `${recordingSeconds.toFixed(1)} / 20.0 ${t("seconds")}`
                        : t("Tap once, speak naturally, then stop.")}
                    </span>
                  </div>
                  {recording && (
                    <div className="record-level" aria-hidden="true">
                      {Array.from({ length: 14 }, (_, index) => <span key={index} />)}
                    </div>
                  )}
                </>
              ) : (
                <div className="recording-review">
                  <div className="recording-ready">
                    <span><CheckCircle2 size={17} /></span>
                    <div>
                      <strong>{t("Voice sample ready")}</strong>
                      <small>{recordedSeconds.toFixed(1)} {t("seconds")} · {(file ? file.size / 1024 / 1024 : 0).toFixed(1)} MB</small>
                    </div>
                  </div>
                  <audio controls preload="metadata" src={recordingUrl}>
                    {t("Your browser does not support audio playback.")}
                  </audio>
                  <button type="button" className="retake-button" onClick={clearRecording}>
                    <RefreshCw size={13} /> {t("Record again")}
                  </button>
                </div>
              )}
              <p className="recorder-privacy">
                <ShieldCheck size={12} />
                {t("Your microphone is used only while recording. Audio is sent when you save the voice.")}
              </p>
            </div>
          ) : (
            <label className={`dropzone ${file ? "dropzone--ready" : ""}`}>
              <input
                type="file"
                accept="audio/wav,audio/mpeg,audio/mp4,audio/x-m4a,audio/webm,audio/ogg,audio/flac"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setError("");
                }}
              />
              {file ? <CheckCircle2 size={23} /> : <UploadCloud size={23} />}
              <span>{file ? file.name : t("Drop or choose an audio clip")}</span>
              <small>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : t("WAV, MP3, M4A, WebM · up to 8 MB")}</small>
            </label>
          )}
          <div className="two-fields">
            <label>
              <span>{t("Voice name")}</span>
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder={t("My narrator")} maxLength={40} />
            </label>
            <label>
              <span>{t("Language")}</span>
              <select value={language} onChange={(event) => setLanguage(event.target.value)}>
                {languages.map((item) => <option key={item} value={item}>{t(item)}</option>)}
              </select>
            </label>
          </div>
          <label className="reference-field">
            <span>{t("Words spoken in the clip")} <small>{t("recommended")}</small></span>
            <textarea
              value={referenceText}
              onChange={(event) => setReferenceText(event.target.value)}
              placeholder={t("Type the exact transcript for the clearest clone…")}
              maxLength={500}
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button
            className="secondary-button full-button"
            onClick={() => void saveVoice()}
            disabled={busy || recording}
          >
            {busy ? <RefreshCw className="spin" size={16} /> : <Sparkles size={16} />}
            {t("Save voice")}
          </button>
        </div>
      )}
    </div>
  );
}

function CommercialDisclosurePage() {
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = localStorage.getItem("surayomi.theme");
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "light" ? "#f4f7f3" : "#07110f");
    localStorage.setItem("surayomi.theme", theme);
  }, [theme]);

  const home = isJapanese ? "/ja/" : "/";
  const alternate = isJapanese
    ? "/commercial-disclosure/"
    : "/ja/commercial-disclosure/";
  const rows: Array<{ label: string; value: ReactNode }> = isJapanese
    ? [
        { label: "販売業者・役務提供事業者", value: "Kei Konuma" },
        { label: "屋号・サービス名", value: "SuraYomi" },
        { label: "運営責任者", value: "Kei Konuma" },
        {
          label: "所在地",
          value: (
            <>
              登録済みの連絡先情報は、請求があった場合に遅滞なく開示します。開示のご請求は
              <a href="mailto:hello@surayomi.app">hello@surayomi.app</a>
              までご連絡ください。
            </>
          ),
        },
        {
          label: "メールアドレス",
          value: <a href="mailto:hello@surayomi.app">hello@surayomi.app</a>,
        },
        {
          label: "提供する役務",
          value:
            "文章からAI音声を生成し、音声デザイン、同意に基づくボイスクローン、設定保存、MP3・WAVダウンロードを提供するウェブサービスです。",
        },
        {
          label: "販売価格・役務の対価",
          value:
            "Free：月50クレジット（無料）。Creator：月額20米ドル／3,000クレジット。Studio：月額40米ドル／8,000クレジット。有効な有料プラン向け追加クレジットは、500クレジット7米ドル、2,000クレジット20米ドルです。ボイスクローンは通常の1.5倍のクレジットを使用します。購入前の最終確認画面に適用価格を表示します。",
        },
        {
          label: "商品代金以外の必要料金",
          value:
            "インターネット接続料金、通信料金、および金融機関が課す為替換算手数料はお客様の負担となります。適用される税金がある場合は購入前に表示します。送料はありません。",
        },
        {
          label: "支払方法",
          value:
            "Stripe Checkout上に表示されるクレジットカード・デビットカードその他の決済方法。",
        },
        {
          label: "支払時期",
          value:
            "初回購入時に決済されます。月額プランは、その後各請求期間の開始時に自動決済されます。",
        },
        {
          label: "役務の提供時期",
          value:
            "決済完了後、原則として直ちにクレジットと有料機能を付与します。システム状況により数分程度かかる場合があります。",
        },
        {
          label: "定期購入・解約",
          value:
            "月額プランは解約されるまで毎月自動更新されます。次回更新日前までにプロフィールの「請求と解約を管理」から解約できます。解約後も当該支払期間の終了までは利用できますが、終了後は更新されず、新しい月間クレジットも付与されません。",
        },
        {
          label: "返品・キャンセル・不具合",
          value:
            "デジタルサービスの性質上、提供開始後の返品は受け付けておらず、お客様都合による途中解約の日割り返金はありません。技術的不具合により購入したサービスを利用できない場合は、発生から7日以内にご連絡ください。調査のうえ、法令に従い再付与または返金等の適切な対応を行います。",
        },
        {
          label: "動作環境",
          value:
            "最新バージョンの主要ウェブブラウザ、安定したインターネット接続、および音声再生に対応した端末が必要です。一部の録音機能にはマイク権限が必要です。",
        },
        {
          label: "準拠法・管轄",
          value:
            "日本法を準拠法とし、本サービスに関する紛争については東京地方裁判所を第一審の専属的合意管轄裁判所とします。",
        },
      ]
    : [
        { label: "Service provider", value: "Kei Konuma" },
        { label: "Trade name and service", value: "SuraYomi" },
        { label: "Head of operations", value: "Kei Konuma" },
        {
          label: "Business address",
          value: (
            <>
              Registered contact details are disclosed without delay upon request. Send
              disclosure requests to{" "}
              <a href="mailto:hello@surayomi.app">hello@surayomi.app</a>.
            </>
          ),
        },
        {
          label: "Email",
          value: <a href="mailto:hello@surayomi.app">hello@surayomi.app</a>,
        },
        {
          label: "Services",
          value:
            "A web-based AI text-to-speech service offering voice design, consent-based voice cloning, saved settings, and MP3 or WAV downloads.",
        },
        {
          label: "Prices",
          value:
            "Free: 50 credits per month at no charge. Creator: US$20 per month for 3,000 credits. Studio: US$40 per month for 8,000 credits. Extra credits for active paid plans are US$7 for 500 credits or US$20 for 2,000 credits. Voice cloning uses 1.5× the standard credit amount. The applicable price is displayed before purchase.",
        },
        {
          label: "Additional fees",
          value:
            "Customers are responsible for internet and data charges and any currency-conversion fees charged by their financial institution. Applicable taxes, if any, are shown before purchase. No shipping fees apply.",
        },
        {
          label: "Payment methods",
          value:
            "Credit cards, debit cards, and other methods displayed in Stripe Checkout.",
        },
        {
          label: "Payment timing",
          value:
            "The first payment is charged at purchase. Monthly plans are then charged automatically at the start of each billing period.",
        },
        {
          label: "Service delivery",
          value:
            "Credits and paid features are ordinarily activated immediately after successful payment, although processing may occasionally take a few minutes.",
        },
        {
          label: "Renewal and cancellation",
          value:
            "Monthly plans renew automatically until canceled. You may cancel from “Manage billing & cancellation” in your profile before the next renewal date. Access continues until the end of the current paid period; the plan does not renew and no new monthly credits are issued after it ends.",
        },
        {
          label: "Returns, refunds, and service issues",
          value:
            "Because this is a digital service, returns are not accepted after delivery and customer-requested cancellations are not prorated. If a technical defect prevents use of a purchased service, contact us within seven days. We will investigate and provide an appropriate remedy, including replacement credits or a refund where required by law.",
        },
        {
          label: "System requirements",
          value:
            "A current major web browser, a stable internet connection, and a device capable of audio playback are required. Recording features also require microphone permission.",
        },
        {
          label: "Governing law and jurisdiction",
          value:
            "Japanese law governs these transactions. The Tokyo District Court has exclusive jurisdiction as the court of first instance for disputes relating to this service.",
        },
      ];

  return (
    <div id="top" className="disclosure-page">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <header className="site-header">
        <div className="header-inner">
          <Logo />
          <div className="header-actions">
            <a
              className="language-switch"
              href={alternate}
              hrefLang={isJapanese ? "en" : "ja"}
              lang={isJapanese ? "en" : "ja"}
            >
              <Globe2 size={13} />
              {isJapanese ? "EN" : "日本語"}
            </a>
            <button
              className="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={t(theme === "dark" ? "Switch to light theme" : "Switch to dark theme")}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <main className="disclosure-main">
        <section className="disclosure-hero">
          <span className="disclosure-kicker"><BookOpenText size={15} /> {isJapanese ? "LEGAL & COMMERCE" : "LEGAL & COMMERCE"}</span>
          <p className="eyebrow">{isJapanese ? "安心してご利用いただくために" : "Clear terms, quietly presented"}</p>
          <h1>{isJapanese ? "特定商取引法に基づく表記" : "Commercial disclosure"}</h1>
          <p>
            {isJapanese
              ? "特定商取引法第11条に基づき、SuraYomiの提供条件と運営情報を掲載しています。"
              : "Information about SuraYomi and its service terms under Japan’s Act on Specified Commercial Transactions."}
          </p>
          <small>{isJapanese ? "最終更新日：2026年7月20日" : "Last updated: July 20, 2026"}</small>
        </section>

        <section className="disclosure-card" aria-label={isJapanese ? "表示事項" : "Disclosure details"}>
          {rows.map((row) => (
            <div className="disclosure-row" key={row.label}>
              <h2>{row.label}</h2>
              <div>{row.value}</div>
            </div>
          ))}
        </section>

        <section className="disclosure-contact">
          <div><ShieldCheck size={22} /></div>
          <div>
            <h2>{isJapanese ? "お問い合わせ・開示請求" : "Questions and disclosure requests"}</h2>
            <p>
              {isJapanese
                ? "通常2〜3営業日以内に返信します。"
                : "We ordinarily reply within two to three business days."}
            </p>
          </div>
          <a href="mailto:hello@surayomi.app">
            hello@surayomi.app <ArrowRight size={15} />
          </a>
        </section>
      </main>

      <footer>
        <div className="footer-main disclosure-footer">
          <div>
            <Logo />
            <p>{t("Beautifully expressive speech,")}<br />{t("crafted from your words.")}</p>
          </div>
          <div className="footer-links">
            <div>
              <strong>{t("Product")}</strong>
              <a href={home}>{t("Studio")}</a>
              <a href={isJapanese ? "/ja/text-to-speech/" : "/text-to-speech/"}>{isJapanese ? "AI音声読み上げ" : "AI text to speech"}</a>
              <a href={isJapanese ? "/ja/voice-cloning/" : "/voice-cloning/"}>{isJapanese ? "AIボイスクローン" : "AI voice cloning"}</a>
              <a href={isJapanese ? "/ja/pricing/" : "/pricing/"}>{t("Pricing")}</a>
            </div>
            <div>
              <strong>{t("Company")}</strong>
              <a href={isJapanese ? "/ja/about/" : "/about/"}>{t("About")}</a>
              <a href={isJapanese ? "/ja/contact/" : "/contact/"}>{t("Contact")}</a>
              <a href={home}>{isJapanese ? "ホーム" : "Home"}</a>
            </div>
            <div>
              <strong>{t("Legal")}</strong>
              <a href={isJapanese ? "/ja/privacy/" : "/privacy/"}>{t("Privacy")}</a>
              <a href={isJapanese ? "/ja/terms/" : "/terms/"}>{t("Terms")}</a>
              <a href={isJapanese ? "/ja/voice-consent/" : "/voice-consent/"}>{t("Voice consent")}</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 SuraYomi.app</span>
          <span>{t("Made with care in Japan")}</span>
        </div>
      </footer>
    </div>
  );
}

function StudioApp() {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(!firebaseConfigured);
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settings, setSettings] = useState<GenerationSettings>(defaultSettings);
  const [text, setText] = useState(samples[isJapanese ? 1 : 0].text);
  const [advanced, setAdvanced] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presets, setPresets] = useState<SavedPreset[]>([]);
  const [cloneVoices, setCloneVoices] = useState<CloneVoice[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [billing, setBilling] = useState<BillingAccount | null>(null);
  const [billingAction, setBillingAction] = useState("");
  const [billingNotice, setBillingNotice] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [audio, setAudio] = useState<{ url: string; filename: string } | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = localStorage.getItem("surayomi.theme");
    if (saved === "dark" || saved === "light") return saved;
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  });
  const billingSyncRef = useRef("");

  const maxCharacters = 500;
  const maxCredits = creditsForText("x".repeat(maxCharacters), settings.engine);
  const textCredits = creditsForText(text, settings.engine);
  const selectedVoice = studioVoices.find((voice) => voice.id === settings.speaker) ?? studioVoices[0];
  const remaining = usage?.remaining ?? 50;
  const currentPlan = usage?.plan ?? "free";
  const billingDate = formatBillingDate(billing?.current_period_end ?? null);
  const hasManageableSubscription = Boolean(
    billing?.can_manage &&
      !["canceled", "incomplete_expired"].includes(billing.subscription_status),
  );
  const billingStatusLine =
    billing?.cancel_at_period_end && billingDate
      ? `${t("Ends")} ${billingDate} · ${t("will not renew")}`
      : currentPlan !== "free" && billingDate
        ? `${t("Renews")} ${billingDate}`
        : billing?.subscription_status === "past_due"
          ? t("Payment needs attention")
          : "";
  const quotaLabel =
    currentPlan === "creator"
      ? t("Creator credits")
      : currentPlan === "studio"
        ? t("Studio credits")
        : t("Free credits");
  const storageKey = `surayomi.presets.${user?.uid ?? "guest"}`;
  const profileName = user ? friendlyProfileName(user) : t("Account");
  const profileFirstName = profileName.split(/\s+/)[0] || t("Account");

  useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser?.emailVerified ? nextUser : null);
      setAuthReady(true);
      if (nextUser?.emailVerified) setAuthOpen(false);
      if (nextUser && !nextUser.emailVerified) setAuthOpen(true);
    });
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "light" ? "#f4f7f3" : "#07110f");
    localStorage.setItem("surayomi.theme", theme);
  }, [theme]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) ?? "[]") as SavedPreset[];
      setPresets(Array.isArray(parsed) ? parsed.slice(0, 12) : []);
    } catch {
      setPresets([]);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!user) {
      setUsage(null);
      setBilling(null);
      setCloneVoices([]);
      return;
    }
    void Promise.allSettled([
      getUsage(user),
      getCloneVoices(user),
      getBillingAccount(user),
    ]).then(([usageResult, voicesResult, billingResult]) => {
      if (usageResult.status === "fulfilled") setUsage(usageResult.value);
      if (billingResult.status === "fulfilled") setBilling(billingResult.value);
      if (voicesResult.status === "fulfilled") {
        const voices = voicesResult.value;
        setCloneVoices(voices);
        if (voices.length && !settings.voiceId) {
          setSettings((current) => ({ ...current, voiceId: voices[0].id }));
        }
      }
      const rejected = [usageResult, voicesResult, billingResult].find(
        (result): result is PromiseRejectedResult => result.status === "rejected",
      );
      setError(
        rejected
          ? rejected.reason instanceof Error
            ? rejected.reason.message
            : t("Account details could not be loaded.")
          : "",
      );
    });
  }, [user]);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const billingResult = currentUrl.searchParams.get("billing");
    const sessionId = currentUrl.searchParams.get("session_id");
    if (billingResult === "canceled") {
      setBillingNotice(t("Checkout was canceled. Nothing was charged."));
      currentUrl.searchParams.delete("billing");
      currentUrl.searchParams.delete("session_id");
      window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      return;
    }
    if (
      billingResult !== "success" ||
      !sessionId ||
      !user ||
      billingSyncRef.current === sessionId
    ) return;
    billingSyncRef.current = sessionId;
    setBillingAction("sync");
    setBillingNotice(t("Confirming your payment…"));
    void syncBillingCheckout(user, sessionId)
      .then(async () => {
        const [nextUsage, nextBilling] = await Promise.all([
          getUsage(user),
          getBillingAccount(user),
        ]);
        setUsage(nextUsage);
        setBilling(nextBilling);
        setBillingNotice(t("Payment confirmed. Your account is ready."));
        currentUrl.searchParams.delete("billing");
        currentUrl.searchParams.delete("session_id");
        window.history.replaceState({}, "", `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      })
      .catch((caught: unknown) => {
        setBillingNotice(
          caught instanceof Error
            ? caught.message
            : t("Payment is still processing. Your account will update automatically."),
        );
      })
      .finally(() => setBillingAction(""));
  }, [user]);

  useEffect(() => {
    return () => {
      if (audio) URL.revokeObjectURL(audio.url);
    };
  }, [audio]);

  const setSetting = <K extends keyof GenerationSettings>(
    key: K,
    value: GenerationSettings[K],
  ) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const changeEngine = (engine: Engine) => {
    setSettings((current) => ({ ...current, engine }));
    setError("");
  };

  const generate = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (!text.trim()) {
      setError(t("Give us a few words to bring to life."));
      return;
    }
    if (settings.engine === "clone" && !settings.voiceId) {
      setError(t("Save or select a cloned voice first."));
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const result = await synthesize(user, text.trim(), settings);
      if (audio) URL.revokeObjectURL(audio.url);
      setAudio({ url: URL.createObjectURL(result.blob), filename: result.filename });
      if (typeof result.remaining === "number" && usage) {
        setUsage({ ...usage, used: usage.limit - result.remaining, remaining: result.remaining });
      } else {
        setUsage(await getUsage(user));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("The voice could not be created."));
    } finally {
      setGenerating(false);
    }
  };

  const savePreset = (name: string) => {
    const next: SavedPreset[] = [
      {
        id: crypto.randomUUID(),
        name,
        createdAt: new Date().toISOString(),
        settings,
      },
      ...presets,
    ].slice(0, 12);
    setPresets(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
    setSavePresetOpen(false);
  };

  const deletePreset = (id: string) => {
    const next = presets.filter((preset) => preset.id !== id);
    setPresets(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const applyPreset = (preset: SavedPreset) => {
    setSettings(preset.settings);
    setPresetOpen(false);
  };

  const createClone = (voice: CloneVoice) => {
    setCloneVoices((current) => [voice, ...current]);
  };

  const removeClone = (id: string) => {
    setCloneVoices((current) => current.filter((voice) => voice.id !== id));
    if (settings.voiceId === id) {
      const replacement = cloneVoices.find((voice) => voice.id !== id);
      setSetting("voiceId", replacement?.id);
    }
  };

  const beginSubscription = async (plan: SubscriptionPlan) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setBillingAction(plan);
    setBillingNotice("");
    try {
      const url = await createSubscriptionCheckout(user, plan, isJapanese ? "/ja/" : "/");
      window.location.assign(url);
    } catch (caught) {
      setBillingNotice(
        caught instanceof Error ? caught.message : t("Checkout could not be opened."),
      );
      setBillingAction("");
    }
  };

  const openBillingPortal = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setBillingAction("portal");
    setBillingNotice("");
    try {
      const url = await createBillingPortal(user, isJapanese ? "/ja/" : "/");
      window.location.assign(url);
    } catch (caught) {
      setBillingNotice(
        caught instanceof Error
          ? caught.message
          : t("Billing settings could not be opened."),
      );
      setBillingAction("");
    }
  };

  const buyCredits = async (pack: CreditPack) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setBillingAction(pack);
    setBillingNotice("");
    try {
      const url = await createCreditCheckout(user, pack, isJapanese ? "/ja/" : "/");
      window.location.assign(url);
    } catch (caught) {
      setBillingNotice(
        caught instanceof Error
          ? caught.message
          : t("Credit checkout could not be opened."),
      );
      setBillingAction("");
    }
  };

  const logout = async () => {
    if (auth) await signOut(auth);
    setProfileOpen(false);
  };

  return (
    <div id="top">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <header className="site-header">
        <div className="header-inner">
          <Logo />
          <nav className={mobileNav ? "nav-open" : ""}>
            <a href="#studio" onClick={() => setMobileNav(false)}>{t("Studio")}</a>
            <a href="#voices" onClick={() => setMobileNav(false)}>{t("Voices")}</a>
            <a href={isJapanese ? "/ja/voice-cloning/" : "/voice-cloning/"} onClick={() => setMobileNav(false)}>
              {isJapanese ? "AIボイスクローン" : "AI voice cloning"}
            </a>
            <a href="#pricing" onClick={() => setMobileNav(false)}>{t("Pricing")}</a>
            <a href={isJapanese ? "/ja/about/" : "/about/"} onClick={() => setMobileNav(false)}>{t("About")}</a>
          </nav>
          <div className="header-actions">
            <a
              className="language-switch"
              href={alternatePath}
              hrefLang={isJapanese ? "en" : "ja"}
              lang={isJapanese ? "en" : "ja"}
              aria-label={isJapanese ? "View in English" : "日本語で表示"}
            >
              <Globe2 size={13} />
              {isJapanese ? "EN" : "日本語"}
            </a>
            <button
              className="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={t(theme === "dark" ? "Switch to light theme" : "Switch to dark theme")}
              title={t(theme === "dark" ? "Switch to light theme" : "Switch to dark theme")}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!authReady ? (
              <span className="header-loader" />
            ) : user ? (
              <div className="profile-wrap">
                <button className="profile-button" onClick={() => setProfileOpen(!profileOpen)}>
                  {user.photoURL ? <img src={user.photoURL} alt="" /> : <UserRound size={17} />}
                  <span>{profileFirstName}</span>
                  <ChevronDown size={14} />
                </button>
                {profileOpen && (
                  <div className="profile-menu">
                    <div className="profile-summary">
                      <strong>{profileName}</strong>
                      <small>{user.email}</small>
                      <span className="profile-plan">{t(
                        currentPlan === "free"
                          ? "Free plan"
                          : currentPlan === "creator"
                            ? "Creator plan"
                            : "Studio plan",
                      )}</span>
                    </div>
                    <div className="profile-quota">
                      <div><span>{t("Available credits")}</span><strong>{formatNumber(usage?.remaining ?? 50)} {t("left")}</strong></div>
                      <div className="quota-track"><span style={{ width: `${usage ? Math.min(100, (usage.used / usage.limit) * 100) : 0}%` }} /></div>
                      {billingStatusLine && <small className="billing-status-line">{billingStatusLine}</small>}
                    </div>
                    {billing?.can_buy_credits && (
                      <div className="profile-refills">
                        <div><Gem size={14} /><span>{t("Add extra credits")}</span></div>
                        <div>
                          <button
                            disabled={Boolean(billingAction)}
                            onClick={() => void buyCredits("credits_500")}
                          >
                            {billingAction === "credits_500" && <RefreshCw className="spin" size={12} />}
                            500 · $7
                          </button>
                          <button
                            disabled={Boolean(billingAction)}
                            onClick={() => void buyCredits("credits_2000")}
                          >
                            {billingAction === "credits_2000" && <RefreshCw className="spin" size={12} />}
                            2,000 · $20
                          </button>
                        </div>
                        <small>{t("Purchased credits do not expire.")}</small>
                      </div>
                    )}
                    {billing?.can_manage && (
                      <button onClick={() => void openBillingPortal()} disabled={Boolean(billingAction)}>
                        {billingAction === "portal"
                          ? <RefreshCw className="spin" size={16} />
                          : <CreditCard size={16} />}
                        {t("Manage billing & cancellation")}
                      </button>
                    )}
                    <button onClick={() => void logout()}><LogOut size={16} /> {t("Sign out")}</button>
                  </div>
                )}
              </div>
            ) : (
              <button className="header-signin" onClick={() => setAuthOpen(true)}>{t("Sign in")}</button>
            )}
            <button className="mobile-menu" onClick={() => setMobileNav(!mobileNav)} aria-label={t("Toggle menu")}>
              {mobileNav ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {billingNotice && (
        <div className="billing-toast" role="status">
          {billingAction === "sync"
            ? <RefreshCw className="spin" size={16} />
            : <CheckCircle2 size={16} />}
          <span>{billingNotice}</span>
          <button onClick={() => setBillingNotice("")} aria-label={t("Close")}>
            <X size={14} />
          </button>
        </div>
      )}

      <main>
        <section className="hero">
          <div className="hero-orbit hero-orbit-one" />
          <div className="hero-orbit hero-orbit-two" />
          <h1>{t("Text, with a")} <em>{t("soul.")}</em></h1>
          <p>
            {t("Create expressive AI text-to-speech. Choose a studio voice, design one from a prompt, or clone your own voice with consent—then refine and download.")}
          </p>
          <div className="hero-proof">
            <span><Languages size={15} /> {t("10 languages")}</span>
            <span><Mic2 size={15} /> {t("Voice cloning")}</span>
            <span><LockKeyhole size={15} /> {t("Private by design")}</span>
          </div>
        </section>

        <section className="studio-shell" id="studio">
          <div className="studio-topbar">
            <div>
              <span>{t("Speech studio")}</span>
              <small>{t("Create, refine, and download")}</small>
            </div>
            <div className="studio-top-actions">
              <div className="preset-wrap">
                <button className="quiet-button" onClick={() => setPresetOpen(!presetOpen)}>
                  <Library size={16} /> {t("Presets")} <ChevronDown size={14} />
                </button>
                {presetOpen && (
                  <PresetMenu presets={presets} onApply={applyPreset} onDelete={deletePreset} />
                )}
              </div>
              <button className="quiet-button" onClick={() => setSavePresetOpen(true)}>
                <Save size={16} /> {t("Save settings")}
              </button>
            </div>
          </div>

          <div className="studio-grid">
            <div className="composer">
              <div className="composer-heading">
                <div>
                  <span className="step-number">01</span>
                  <div><h2>{t("Write your words")}</h2><p>{t("Paste a script or begin with a sample.")}</p></div>
                </div>
                <div className={`character-count ${text.length > maxCharacters ? "over" : ""}`}>
                  {formatNumber(textCredits)} <span>/ {formatNumber(maxCredits)} {t("credits")}</span>
                </div>
              </div>
              <div className="editor-wrap">
                <textarea
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={t("Write something worth hearing…")}
                  maxLength={maxCharacters}
                  spellCheck
                />
                <div className="editor-foot">
                  <div className="sample-row">
                    <span>{t("Try a sample")}</span>
                    {samples.map((sample) => (
                      <button key={sample.label} onClick={() => setText(sample.text)}>
                        {t(sample.label)}
                      </button>
                    ))}
                  </div>
                  <button className="icon-button" onClick={() => void navigator.clipboard.writeText(text)} aria-label={t("Copy text")}>
                    <Copy size={16} />
                  </button>
                </div>
              </div>

              <div className="section-divider" />
              <div className="composer-heading voice-heading" id="voices">
                <div>
                  <span className="step-number">02</span>
                  <div><h2>{t("Shape the voice")}</h2><p>{t("Start familiar, invent freely, or preserve a real voice.")}</p></div>
                </div>
              </div>
              <div className="mode-grid">
                <ModeButton
                  active={settings.engine === "custom"}
                  icon={<Headphones size={19} />}
                  label={t("Studio voice")}
                  caption={t("Curated & consistent")}
                  onClick={() => changeEngine("custom")}
                />
                <ModeButton
                  active={settings.engine === "design"}
                  icon={<WandSparkles size={19} />}
                  label={t("Design a voice")}
                  caption={t("Describe any sound")}
                  onClick={() => changeEngine("design")}
                />
                <ModeButton
                  active={settings.engine === "clone"}
                  icon={<AudioLines size={19} />}
                  label={t("Clone a voice")}
                  caption={t("Recreate with consent")}
                  onClick={() => changeEngine("clone")}
                />
              </div>

              {settings.engine === "custom" && (
                <div className="voice-selector">
                  <div className="field-label-row">
                    <span className="field-label">{t("Selected voice")}</span>
                    <span className="voice-count">{studioVoices.length} {t("voices")}</span>
                  </div>
                  <div className="voice-current">
                    <span className="voice-avatar" style={{ "--voice-color": selectedVoice.color } as React.CSSProperties}>
                      {selectedVoice.initials}
                    </span>
                    <div>
                      <strong>{selectedVoice.name}</strong>
                      <p>{t(selectedVoice.description)}</p>
                      <small><Globe2 size={12} /> {t(selectedVoice.language)} · {t(selectedVoice.accent)}</small>
                    </div>
                    <select
                      aria-label={t("Choose studio voice")}
                      value={settings.speaker}
                      onChange={(event) => setSetting("speaker", event.target.value)}
                    >
                      {studioVoices.map((voice) => <option key={voice.id} value={voice.id}>{voice.name}</option>)}
                    </select>
                    <ChevronDown size={16} />
                  </div>
                </div>
              )}

              {settings.engine === "design" && (
                <div className="design-panel">
                  <div className="design-note"><Sparkles size={17} /><span>{t("Describe a person, mood, accent, texture, or performance direction. A voice will be designed for this read.")}</span></div>
                  <label>
                    <span className="field-label">{t("Voice description")}</span>
                    <textarea
                      value={settings.prompt}
                      onChange={(event) => setSetting("prompt", event.target.value)}
                      placeholder={t("A warm Japanese woman in her thirties, close-mic, soft smile, calm documentary pacing…")}
                      maxLength={500}
                    />
                  </label>
                  <div className="prompt-chips">
                    {["Warm storyteller", "Bright anime heroine", "Deep cinematic narrator", "Gentle audiobook"].map((prompt) => (
                      <button key={prompt} onClick={() => setSetting("prompt", t(prompt))}>{t(prompt)}</button>
                    ))}
                  </div>
                </div>
              )}

              {settings.engine === "clone" && (
                <VoiceClonePanel
                  user={user}
                  voices={cloneVoices}
                  selectedId={settings.voiceId}
                  onSelect={(id) => setSetting("voiceId", id)}
                  onCreated={createClone}
                  onDeleted={removeClone}
                  onSignIn={() => setAuthOpen(true)}
                />
              )}

              {settings.engine === "custom" && (
                <label className="direction-field">
                  <div className="field-label-row">
                    <span className="field-label">{t("Performance direction")} <small>{t("optional")}</small></span>
                    <span>{settings.prompt.length}/500</span>
                  </div>
                  <textarea
                    value={settings.prompt}
                    onChange={(event) => setSetting("prompt", event.target.value)}
                    placeholder={t("Read intimately, with a little wonder…")}
                    maxLength={500}
                  />
                </label>
              )}
            </div>

            <aside className="control-panel">
              <div className="control-head">
                <div><Settings2 size={18} /><span>{t("Voice controls")}</span></div>
                <button
                  className="icon-button"
                  onClick={() => setSettings({ ...defaultSettings, engine: settings.engine, voiceId: settings.voiceId })}
                  aria-label={t("Reset controls")}
                  title={t("Reset controls")}
                >
                  <RefreshCw size={15} />
                </button>
              </div>
              <div className="control-body">
                <label className="select-field">
                  <span><Languages size={15} /> {t("Language")}</span>
                  <select value={settings.language} onChange={(event) => setSetting("language", event.target.value)}>
                    {languages.map((language) => <option key={language} value={language}>{t(language)}</option>)}
                  </select>
                </label>
                <RangeField
                  label={t("Speaking pace")}
                  value={settings.speed}
                  min={0.6}
                  max={1.6}
                  step={0.05}
                  display={`${settings.speed.toFixed(2)}×`}
                  onChange={(value) => setSetting("speed", value)}
                />
                <RangeField
                  label={t("Expressiveness")}
                  hint={t("Higher values introduce more variation.")}
                  value={settings.temperature}
                  min={0.2}
                  max={1.4}
                  step={0.05}
                  display={t(settings.temperature < 0.55 ? "Steady" : settings.temperature > 1 ? "Vivid" : "Natural")}
                  onChange={(value) => setSetting("temperature", value)}
                />
                <div className="seed-field">
                  <div>
                    <span>{t("Voice seed")} <span className="tiny-hint" title={t("Reuse the same seed and settings for consistent output.")}>?</span></span>
                    <small>{t("Lock this number to reproduce a voice.")}</small>
                  </div>
                  <div>
                    <input
                      type="number"
                      min={0}
                      max={2_147_483_647}
                      value={settings.seed}
                      onChange={(event) => setSetting("seed", Math.max(0, Number(event.target.value)))}
                    />
                    <button
                      className="icon-button"
                      onClick={() => setSetting("seed", Math.floor(Math.random() * 99_999_999))}
                      aria-label={t("Randomize seed")}
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                </div>
                <button className="advanced-toggle" onClick={() => setAdvanced(!advanced)}>
                  <span><Gauge size={15} /> {t("Advanced")}</span>
                  <ChevronDown className={advanced ? "rotated" : ""} size={15} />
                </button>
                {advanced && (
                  <div className="advanced-fields">
                    <RangeField
                      label={t("Focus")}
                      value={settings.topP}
                      min={0.5}
                      max={1}
                      step={0.01}
                      display={settings.topP.toFixed(2)}
                      onChange={(value) => setSetting("topP", value)}
                    />
                    <RangeField
                      label={t("Vocabulary range")}
                      value={settings.topK}
                      min={10}
                      max={100}
                      step={5}
                      display={String(settings.topK)}
                      onChange={(value) => setSetting("topK", value)}
                    />
                    <RangeField
                      label={t("Repetition guard")}
                      value={settings.repetitionPenalty}
                      min={1}
                      max={1.3}
                      step={0.01}
                      display={settings.repetitionPenalty.toFixed(2)}
                      onChange={(value) => setSetting("repetitionPenalty", value)}
                    />
                  </div>
                )}
                <div className="format-field">
                  <span>{t("Download format")}</span>
                  <div>
                    {(["mp3", "wav"] as const).map((format) => (
                      <button
                        key={format}
                        className={settings.format === format ? "active" : ""}
                        onClick={() => setSetting("format", format)}
                      >
                        {format.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="quota-card">
                <div>
                  <span>{quotaLabel}</span>
                  <strong>{formatNumber(remaining)} {t("left")}</strong>
                </div>
                <div className="quota-track"><span style={{ width: `${usage ? Math.min(100, (usage.used / usage.limit) * 100) : 0}%` }} /></div>
                <small>
                  {billingStatusLine ||
                    t(currentPlan === "free"
                      ? "Free credits reset monthly"
                      : "Included credits reset each billing period")}
                </small>
              </div>
            </aside>
          </div>

          <div className="generate-zone">
            {error && <div className="generation-error"><CircleHelp size={17} /><span>{error}</span><button onClick={() => setError("")}><X size={15} /></button></div>}
            {generating && (
              <div className="generating-card">
                <div className="generating-copy">
                  <span className="generating-icon"><AudioLines size={19} /></span>
                  <div><strong>{t("Giving your words a voice…")}</strong><small>{t("Shaping tone, rhythm, and breath.")}</small></div>
                </div>
                <Waveform active />
              </div>
            )}
            {audio && !generating && (
              <AudioResult
                url={audio.url}
                filename={audio.filename}
                onClear={() => {
                  URL.revokeObjectURL(audio.url);
                  setAudio(null);
                }}
              />
            )}
            {!audio && !generating && (
              <div className="generate-preview">
                <div>
                  <span className="preview-orb"><Play size={18} fill="currentColor" /></span>
                  <div><strong>{t("Your audio will appear here")}</strong><small>{t("Listen, compare, and download without leaving the studio.")}</small></div>
                </div>
                <Waveform />
              </div>
            )}
            <div className="generate-actions">
              <div className="generation-meta">
                <span><Clock3 size={14} /> ~{Math.max(2, Math.round(text.length / 18))} {t("sec audio")}</span>
                <span><Zap size={14} /> {formatNumber(textCredits)} {t("credits")}</span>
              </div>
              <button
                className="generate-button"
                onClick={() => void generate()}
                disabled={generating || text.length === 0 || text.length > maxCharacters}
              >
                {generating ? <RefreshCw className="spin" size={19} /> : <Sparkles size={19} />}
                {t(user ? "Create speech" : "Sign in & create")}
                {!generating && <ArrowRight size={18} />}
              </button>
            </div>
          </div>
        </section>

        <section className="trust-strip">
          <div><ShieldCheck size={19} /><span><strong>{t("Private by design")}</strong>{t("Your voice data moves only when you choose to create.")}</span></div>
          <div><Headphones size={19} /><span><strong>{t("Made to refine")}</strong>{t("Listen, adjust, and download in one quiet workspace.")}</span></div>
          <div><RefreshCw size={19} /><span><strong>{t("Return to a sound")}</strong>{t("Presets and seeds make favorite voices easy to revisit.")}</span></div>
        </section>

        <section className="story-section" id="about">
          <div className="section-kicker"><span /> {t("BUILT FOR THE WAY STORIES SOUND")}</div>
          <div className="story-grid">
            <div className="story-copy">
              <h2>{t("A voice is more than a dropdown.")}</h2>
              <p>
                {t("SuraYomi gives you the useful controls without making speech feel mechanical. Start with a performer, write a direction in everyday language, then preserve the exact settings when the result feels right.")}
              </p>
              <div className="story-points">
                <div><span>01</span><p><strong>{t("Describe naturally")}</strong>{t("Ask for a smoky whisper, an excited guide, or anything between.")}</p></div>
                <div><span>02</span><p><strong>{t("Refine precisely")}</strong>{t("Tune pace, expression, focus, consistency, and format.")}</p></div>
                <div><span>03</span><p><strong>{t("Return anytime")}</strong>{t("Saved presets and voice seeds make your sound reproducible.")}</p></div>
              </div>
            </div>
            <div className="story-visual">
              <div className="story-card story-card-one">
                <span className="story-icon"><BookOpenText size={21} /></span>
                <p>{t("“Read this as if you’ve just discovered something wonderful.”")}</p>
                <span className="story-tag">{t("Performance direction")}</span>
              </div>
              <div className="voice-rings">
                <div className="ring ring-one" />
                <div className="ring ring-two" />
                <div className="ring ring-three" />
                <BrandMark />
              </div>
              <div className="story-card story-card-two">
                <div className="seed-chip"><RefreshCw size={14} /> {t("Voice seed")} 2407</div>
                <strong>{t("Warm documentary")}</strong>
                <div className="small-wave">{Array.from({ length: 18 }, (_, i) => <span key={i} style={{ height: `${6 + ((i * 13) % 18)}px` }} />)}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="section-kicker centered"><span /> {t("SIMPLE, HONEST PRICING")} <span /></div>
          <h2>{t("Begin free. Grow when you’re ready.")}</h2>
          <p className="pricing-lead">{t("More room to create, with secure billing and cancellation anytime.")}</p>
          <div className="pricing-grid">
            {pricing.map((plan) => {
              const isCurrent = Boolean(user) && plan.id === currentPlan;
              const isFree = plan.id === "free";
              const isLoading = Boolean(user && !billing);
              const shouldManage = hasManageableSubscription || (isFree && currentPlan !== "free");
              const label = !user
                ? t(isFree ? "Start free" : `Choose ${plan.name}`)
                : isLoading
                  ? t("Loading account…")
                  : billing?.enabled === false
                    ? t("Billing temporarily unavailable")
                    : isCurrent
                      ? t(isFree ? "Plan active" : "Manage subscription")
                      : shouldManage
                        ? t("Change in billing")
                        : t(`Choose ${plan.name}`);
              return (
                <article className={`price-card ${plan.featured ? "price-card--featured" : ""}`} key={plan.name}>
                  {plan.featured && <div className="coming-ribbon">{t("RECOMMENDED")}</div>}
                  <div className="price-name">
                    <span>{plan.name}</span>
                    {isCurrent ? <small>{t("CURRENT PLAN")}</small> : null}
                  </div>
                  <div className="price"><strong>{plan.price}</strong><span>{t(plan.cadence)}</span></div>
                  <p>{t(plan.description)}</p>
                  <ul>
                    {plan.features.map((feature) => <li key={feature}><Check size={15} />{t(feature)}</li>)}
                  </ul>
                  <button
                    className={isCurrent ? "plan-button plan-button--current" : "plan-button"}
                    disabled={
                      Boolean(billingAction) ||
                      isLoading ||
                      (Boolean(user) && billing?.enabled === false) ||
                      (Boolean(user) && isFree && currentPlan === "free")
                    }
                    onClick={() => {
                      if (!user) {
                        setAuthOpen(true);
                      } else if (shouldManage || (isCurrent && !isFree)) {
                        void openBillingPortal();
                      } else if (!isFree) {
                        void beginSubscription(plan.id);
                      }
                    }}
                  >
                    {billingAction === plan.id || (billingAction === "portal" && (shouldManage || isCurrent))
                      ? <RefreshCw className="spin" size={14} />
                      : null}
                    {label}
                  </button>
                </article>
              );
            })}
          </div>
          <div className="pricing-note">
            <LockKeyhole size={15} />
            {t("Secure checkout by Stripe. Plans renew monthly until canceled; cancel anytime in your profile.")}
          </div>
        </section>

        <section className="closing-cta">
          <div className="closing-wave" aria-hidden>
            {Array.from({ length: 44 }, (_, index) => <span key={index} style={{ height: `${12 + ((index * 23) % 70)}px` }} />)}
          </div>
          <div>
            <p className="eyebrow">{t("YOUR NEXT LINE IS WAITING")}</p>
            <h2>{t("Make it heard.")}</h2>
            <p>{t("50 credits a month. No card. No complicated setup.")}</p>
            <button className="primary-button" onClick={() => {
              if (!user) setAuthOpen(true);
              document.querySelector("#studio")?.scrollIntoView({ behavior: "smooth" });
            }}>
              {t(user ? "Open the studio" : "Create your free account")} <ArrowRight size={17} />
            </button>
          </div>
        </section>
      </main>

      <footer>
        <div className="footer-main">
          <div>
            <Logo />
            <p>{t("Beautifully expressive speech,")}<br />{t("crafted from your words.")}</p>
          </div>
          <div className="footer-links">
            <div><strong>{t("Product")}</strong><a href="#studio">{t("Studio")}</a><a href={isJapanese ? "/ja/text-to-speech/" : "/text-to-speech/"}>{isJapanese ? "AI音声読み上げ" : "AI text to speech"}</a><a href={isJapanese ? "/ja/voice-design/" : "/voice-design/"}>{t("Design a voice")}</a><a href={isJapanese ? "/ja/voice-cloning/" : "/voice-cloning/"}>{isJapanese ? "AIボイスクローン" : "AI voice cloning"}</a><a href={isJapanese ? "/ja/pricing/" : "/pricing/"}>{t("Pricing")}</a></div>
            <div><strong>{t("Company")}</strong><a href={isJapanese ? "/ja/about/" : "/about/"}>{t("About")}</a><a href={isJapanese ? "/ja/contact/" : "/contact/"}>{t("Contact")}</a><a href={isJapanese ? "/ja/faq/" : "/faq/"}>{isJapanese ? "よくある質問" : "FAQ"}</a></div>
            <div><strong>{t("Legal")}</strong><a href={isJapanese ? "/ja/privacy/" : "/privacy/"}>{t("Privacy")}</a><a href={isJapanese ? "/ja/terms/" : "/terms/"}>{t("Terms")}</a><a href={isJapanese ? "/ja/commercial-disclosure/" : "/commercial-disclosure/"}>{t("Commercial disclosure")}</a><a href={isJapanese ? "/ja/voice-consent/" : "/voice-consent/"}>{t("Voice consent")}</a></div>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 SuraYomi.app</span>
          <span>{t("Made with care in Japan")}</span>
        </div>
      </footer>

      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
      {savePresetOpen && <SavePresetModal onSave={savePreset} onClose={() => setSavePresetOpen(false)} />}
    </div>
  );
}

export default function App() {
  const disclosurePath = /^\/(?:ja\/)?commercial-disclosure\/?$/.test(window.location.pathname);
  return disclosurePath ? <CommercialDisclosurePage /> : <StudioApp />;
}
